import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Upload, Check, FileDown } from "lucide-react";
import { SignaturePad } from "@/components/SignaturePad";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FIELD = "w-full bg-white border border-[#E3DECE] px-4 py-3 text-sm focus:outline-none focus:border-[#BA6D52] transition-colors";
const LABEL = "block text-[10px] uppercase tracking-[0.18em] text-[#757067] mb-2";

const DOCS = (isAT = false, isAusland = false) => [
  { key: "gewerbeanmeldung",
    label: isAusland ? "Gewerbe- / Unternehmensnachweis" : isAT ? "Gewerbeschein" : "Gewerbeanmeldung",
    hint: isAusland ? "Pflicht — Nachweis deiner Selbstständigkeit aus deinem Heimatland — oder „wird nachgereicht\u201c ankreuzen"
      : isAT ? "Pflicht — Kopie deines Gewerbescheins (WKO / Gewerbebehörde) — oder „wird nachgereicht\u201c ankreuzen"
             : "Pflicht — Kopie deiner Anmeldung beim Gewerbeamt — oder „wird nachgereicht\u201c ankreuzen", required: true },
  { key: "lebenslauf", label: "Lebenslauf", hint: "Pflicht — gerne mit Stationen und Referenzen", required: true },
  { key: "gesundheitszeugnis",
    label: isAusland ? "Hygieneschulung / Gesundheitsnachweis" : isAT ? "Hygieneschulung (LMSVG)" : "Belehrung § 43 IfSG",
    hint: isAusland ? "Nachweis der Lebensmittelhygiene aus deinem Heimatland — oder unten „wird nachgereicht\u201c ankreuzen"
      : isAT ? "Nachweis der Lebensmittelhygiene-Schulung — oder unten „wird nachgereicht\u201c ankreuzen"
             : "Gesundheitszeugnis / Erstbelehrung — oder unten „wird nachgereicht\u201c ankreuzen", required: true },
];

export default function VermittlungKoch() {
  const [stamm, setStamm] = useState({ bereiche: {}, positionen: {} });
  const [form, setForm] = useState({
    vorname: "", nachname: "", email: "", telefon: "", strasse: "", plz: "", ort: "", land: "DE",
    umkreis_km: 50, erfahrung_jahre: "", stundensatz_ab_eur: "", tagessatz_ab_eur: "",
    sucht_tageseinsaetze: true, sucht_festanstellung: false, sucht_privatkunden: false, wunschgehalt_brutto_monat: "",
    fuehrerschein: false, eigenes_werkzeug: false, verfuegbar_ab: "", ueber_mich: "",
    dsgvo_consent: false, signature_name: "",
  });
  const [bereiche, setBereiche] = useState([]);
  const [positionen, setPositionen] = useState([]);
  const [files, setFiles] = useState({});
  const [ifsgLater, setIfsgLater] = useState(false);
  const [gewerbeLater, setGewerbeLater] = useState(false);
  const [signature, setSignature] = useState("");
  const [vertragMode, setVertragMode] = useState("digital"); // digital | upload
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  const isAT = form.land === "AT";
  const isDE = form.land === "DE";
  const isAusland = !isDE && !isAT;
  const docs = DOCS(isAT, isAusland);

  useEffect(() => {
    axios.get(`${API}/vermittlung/stammdaten`).then((r) => setStamm(r.data)).catch(() => {});
  }, []);

  const laterRef = useRef(false);
  const docsRef = useRef(DOCS(false));
  useEffect(() => { laterRef.current = ifsgLater; }, [ifsgLater]);
  useEffect(() => { docsRef.current = docs; }, [docs]);

  // Datei irgendwo auf der Seite abgelegt: Browser darf sie NICHT öffnen —
  // stattdessen dem ersten noch leeren Pflicht-Upload zuordnen.
  useEffect(() => {
    const allow = ["pdf", "jpg", "jpeg", "png", "webp"];
    const over = (e) => e.preventDefault();
    const drop = (e) => {
      e.preventDefault();
      if (e.target.closest?.("[data-file-drop]")) return; // eigenes Feld hat es schon übernommen
      const f = e.dataTransfer?.files?.[0];
      if (!f) return;
      if (!allow.includes(f.name.split(".").pop()?.toLowerCase())) {
        return toast.error("Bitte eine PDF-Datei oder ein Foto (JPG, PNG, WEBP) ablegen");
      }
      setFiles((prev) => {
        const slot = docsRef.current.find((d) => !prev[d.key] && !(d.key === "gesundheitszeugnis" && laterRef.current));
        if (!slot) {
          toast.info("Alle Unterlagen sind schon ausgewählt — bitte direkt im gewünschten Feld ablegen");
          return prev;
        }
        toast.success(`„${f.name}" als ${slot.label} übernommen`);
        return { ...prev, [slot.key]: f };
      });
    };
    window.addEventListener("dragover", over);
    window.addEventListener("drop", drop);
    return () => {
      window.removeEventListener("dragover", over);
      window.removeEventListener("drop", drop);
    };
  }, []);

  const set = (k) => (e) => {
    const v = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [k]: v }));
  };

  const toggle = (list, setList, key) =>
    setList(list.includes(key) ? list.filter((x) => x !== key) : [...list, key]);

  const submit = async (e) => {
    e.preventDefault();
    if (!bereiche.length) return toast.error("Bitte mindestens einen Bereich auswählen");
    if (!positionen.length) return toast.error("Bitte mindestens eine Position auswählen");
    if (!form.dsgvo_consent) return toast.error("Bitte die Datenschutzhinweise bestätigen");
    const plz = form.plz.trim();
    if (isDE || isAT) {
      const plzLen = isAT ? 4 : 5;
      if (!/^\d+$/.test(plz) || plz.length !== plzLen)
        return toast.error(`Bitte eine ${plzLen}-stellige Postleitzahl für ${isAT ? "Österreich" : "Deutschland"} angeben`);
    } else if (plz.length < 3) {
      return toast.error("Bitte deine Postleitzahl angeben");
    }
    for (const d of docs) {
      if (d.key === "gesundheitszeugnis" && ifsgLater) continue;
      if (d.key === "gewerbeanmeldung" && gewerbeLater) continue;
      if (!files[d.key]) return toast.error(`Bitte ${d.label} hochladen`);
    }
    if (vertragMode === "digital" && !signature) return toast.error("Bitte den Vertrag im Feld unterschreiben");
    if (vertragMode === "upload" && !files.vermittlungsvertrag) return toast.error("Bitte den unterschriebenen Vertrag hochladen");

    setBusy(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => {
        if (v !== "" && v !== null && v !== undefined) fd.append(k, v);
      });
      fd.append("bereiche", bereiche.join(","));
      fd.append("positionen", positionen.join(","));
      docs.forEach((d) => files[d.key] && fd.append(d.key, files[d.key]));
      fd.append("gesundheitszeugnis_nachreichen", ifsgLater);
      fd.append("gewerbe_nachreichen", gewerbeLater);
      if (vertragMode === "upload") fd.append("vermittlungsvertrag", files.vermittlungsvertrag);
      else fd.append("signature_data_url", signature);

      const r = await axios.post(`${API}/vermittlung/koeche`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 120000,
      });
      setDone(r.data.chef_number);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Registrierung fehlgeschlagen — bitte erneut versuchen");
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-24 md:py-32" data-testid="koch-register-success">
        <Check className="w-10 h-10 text-[#BA6D52]" strokeWidth={1.4} />
        <h1 className="font-serif text-4xl sm:text-5xl font-light tracking-tighter mt-8">Alles angekommen.</h1>
        <p className="text-[#575349] mt-6 leading-relaxed">
          Danke — deine Registrierung liegt mir vor. Deine Koch-Nummer ist{" "}
          <b className="text-[#2C2A26]">{done}</b>. Ich prüfe deine Unterlagen persönlich und melde mich
          innerhalb von 2 Werktagen. Eine Bestätigung ist gerade an deine E-Mail-Adresse unterwegs.
        </p>
        <p className="text-sm text-[#757067] mt-6 leading-relaxed">
          Für dich ist die Vermittlung dauerhaft kostenlos — die Provision zahlt immer der Betrieb.
        </p>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-6 md:px-12 py-16 md:py-24" data-testid="koch-register-page">
      <p className="text-[10px] uppercase tracking-[0.35em] text-[#BA6D52]">Für selbstständige Köche</p>
      <h1 className="font-serif text-4xl sm:text-5xl font-light tracking-tighter mt-6">Als Mietkoch registrieren</h1>
      <p className="text-[#757067] font-light text-lg mt-6 leading-relaxed">
        Einmal ausfüllen, dann melde ich mich, wenn ein Einsatz zu dir passt. Für dich kostenlos —
        die Provision trägt immer der Betrieb.
      </p>

      <form onSubmit={submit} className="mt-14 space-y-12">
        <section>
          <h2 className="text-base md:text-lg font-serif border-b border-[#E3DECE] pb-3">Deine Daten</h2>
          <div className="grid sm:grid-cols-2 gap-5 mt-6">
            <div><label className={LABEL}>Vorname *</label><input className={FIELD} required value={form.vorname} onChange={set("vorname")} data-testid="koch-vorname" /></div>
            <div><label className={LABEL}>Nachname *</label><input className={FIELD} required value={form.nachname} onChange={set("nachname")} data-testid="koch-nachname" /></div>
            <div><label className={LABEL}>E-Mail *</label><input type="email" className={FIELD} required value={form.email} onChange={set("email")} data-testid="koch-email" /></div>
            <div><label className={LABEL}>Telefon *</label><input className={FIELD} required value={form.telefon} onChange={set("telefon")} data-testid="koch-telefon" /></div>
            <div className="sm:col-span-2"><label className={LABEL}>Straße & Hausnummer</label><input className={FIELD} value={form.strasse} onChange={set("strasse")} data-testid="koch-strasse" /></div>
            <div>
              <label className={LABEL}>Land *</label>
              <select className={FIELD} value={form.land} onChange={set("land")} data-testid="koch-land">
                <option value="DE">Deutschland</option>
                <option value="AT">Österreich</option>
                <option value="CH">Schweiz</option>
                <option value="PL">Polen</option>
                <option value="CZ">Tschechien</option>
                <option value="SK">Slowakei</option>
                <option value="HU">Ungarn</option>
                <option value="SI">Slowenien</option>
                <option value="HR">Kroatien</option>
                <option value="RO">Rumänien</option>
                <option value="BG">Bulgarien</option>
                <option value="IT">Italien</option>
                <option value="FR">Frankreich</option>
                <option value="ES">Spanien</option>
                <option value="PT">Portugal</option>
                <option value="NL">Niederlande</option>
                <option value="BE">Belgien</option>
                <option value="LU">Luxemburg</option>
                <option value="DK">Dänemark</option>
                <option value="SE">Schweden</option>
                <option value="LT">Litauen</option>
                <option value="LV">Lettland</option>
                <option value="EE">Estland</option>
                <option value="GR">Griechenland</option>
                <option value="IE">Irland</option>
                <option value="FI">Finnland</option>
              </select>
            </div>
            <div><label className={LABEL}>PLZ *{isDE ? " (5-stellig)" : isAT ? " (4-stellig)" : ""}</label>
              <input className={FIELD} required inputMode={isDE || isAT ? "numeric" : "text"}
                maxLength={isDE ? 5 : isAT ? 4 : 10}
                value={form.plz} onChange={set("plz")} data-testid="koch-plz" /></div>
            <div><label className={LABEL}>Ort *</label><input className={FIELD} required value={form.ort} onChange={set("ort")} data-testid="koch-ort" /></div>
          </div>
        </section>

        <section>
          <h2 className="text-base md:text-lg font-serif border-b border-[#E3DECE] pb-3">Einsatzgebiet & Profil</h2>
          <div className="grid sm:grid-cols-2 gap-5 mt-6">
            <div>
              <label className={LABEL}>Umkreis, den du abdeckst *</label>
              <select className={FIELD} value={form.umkreis_km} onChange={set("umkreis_km")} data-testid="koch-umkreis">
                {[25, 50, 75, 100, 150, 200, 300].map((k) => <option key={k} value={k}>{k} km</option>)}
              </select>
            </div>
            <div><label className={LABEL}>Berufserfahrung (Jahre)</label><input type="number" min="0" className={FIELD} value={form.erfahrung_jahre} onChange={set("erfahrung_jahre")} data-testid="koch-erfahrung" /></div>
            <div><label className={LABEL}>Dein Stundensatz ab (€ netto)</label><input type="number" step="0.5" min="0" className={FIELD} value={form.stundensatz_ab_eur} onChange={set("stundensatz_ab_eur")} data-testid="koch-stundensatz" /></div>
            <div><label className={LABEL}>Dein Tagessatz ab (€ netto)</label><input type="number" step="5" min="0" className={FIELD} value={form.tagessatz_ab_eur} onChange={set("tagessatz_ab_eur")} data-testid="koch-tagessatz" /></div>
            <div><label className={LABEL}>Verfügbar ab</label><input type="date" className={FIELD} value={form.verfuegbar_ab} onChange={set("verfuegbar_ab")} data-testid="koch-verfuegbar" /></div>
          </div>

          <p className={`${LABEL} mt-8`}>In welchen Bereichen siehst du dich? *</p>
          <div className="flex flex-wrap gap-2">
            {Object.entries(stamm.bereiche || {}).map(([k, label]) => (
              <button type="button" key={k} onClick={() => toggle(bereiche, setBereiche, k)}
                data-testid={`koch-bereich-${k}`}
                className={`px-4 py-2 text-xs border transition-colors ${bereiche.includes(k) ? "bg-[#BA6D52] text-white border-[#BA6D52]" : "bg-white border-[#E3DECE] text-[#575349] hover:border-[#BA6D52]"}`}>
                {label}
              </button>
            ))}
          </div>

          <p className={`${LABEL} mt-8`}>Auf welchen Positionen arbeitest du? *</p>
          <div className="flex flex-wrap gap-2">
            {Object.entries(stamm.positionen || {}).map(([k, label]) => (
              <button type="button" key={k} onClick={() => toggle(positionen, setPositionen, k)}
                data-testid={`koch-position-${k}`}
                className={`px-4 py-2 text-xs border transition-colors ${positionen.includes(k) ? "bg-[#BA6D52] text-white border-[#BA6D52]" : "bg-white border-[#E3DECE] text-[#575349] hover:border-[#BA6D52]"}`}>
                {label}
              </button>
            ))}
          </div>

          <div className="mt-8 space-y-3">
            <label className="flex items-start gap-3 text-sm text-[#575349] cursor-pointer">
              <input type="checkbox" className="mt-1 accent-[#BA6D52]" checked={form.sucht_tageseinsaetze} onChange={set("sucht_tageseinsaetze")} data-testid="koch-sucht-tage" />
              <span>Ich suche <b>Tageseinsätze</b> als selbstständiger Mietkoch</span>
            </label>
            <label className="flex items-start gap-3 text-sm text-[#575349] cursor-pointer">
              <input type="checkbox" className="mt-1 accent-[#BA6D52]" checked={form.sucht_festanstellung} onChange={set("sucht_festanstellung")} data-testid="koch-sucht-fest" />
              <span>Ich bin auch offen für eine <b>Festanstellung</b></span>
            </label>
            {form.sucht_festanstellung && (
              <div className="pl-7 pt-2 max-w-xs">
                <label className={LABEL}>Wunschgehalt (€ brutto/Monat)</label>
                <input type="number" step="100" className={FIELD} value={form.wunschgehalt_brutto_monat} onChange={set("wunschgehalt_brutto_monat")} data-testid="koch-wunschgehalt" />
              </div>
            )}
            <label className="flex items-start gap-3 text-sm text-[#575349] cursor-pointer">
              <input type="checkbox" className="mt-1 accent-[#BA6D52]" checked={form.sucht_privatkunden} onChange={set("sucht_privatkunden")} data-testid="koch-sucht-privat" />
              <span>Ich koche auch für <b>Privatkunden</b> (Private Dining, Geburtstage, Familienfeiern)</span>
            </label>
            <label className="flex items-start gap-3 text-sm text-[#575349] cursor-pointer">
              <input type="checkbox" className="mt-1 accent-[#BA6D52]" checked={form.fuehrerschein} onChange={set("fuehrerschein")} data-testid="koch-fuehrerschein" />
              <span>Ich habe Führerschein und bin mobil</span>
            </label>
            <label className="flex items-start gap-3 text-sm text-[#575349] cursor-pointer">
              <input type="checkbox" className="mt-1 accent-[#BA6D52]" checked={form.eigenes_werkzeug} onChange={set("eigenes_werkzeug")} data-testid="koch-werkzeug" />
              <span>Ich bringe eigenes Werkzeug und eigene Berufskleidung mit</span>
            </label>
          </div>

          <div className="mt-8">
            <label className={LABEL}>Über dich — was solltest ich wissen?</label>
            <textarea rows={4} className={FIELD} value={form.ueber_mich} onChange={set("ueber_mich")} data-testid="koch-ueber-mich"
              placeholder="Stationen, Spezialgebiete, was du besonders gut kannst, was du nicht machen möchtest …" />
          </div>
        </section>

        <section>
          <h2 className="text-base md:text-lg font-serif border-b border-[#E3DECE] pb-3">Unterlagen</h2>
          <p className="text-sm text-[#757067] mt-4 leading-relaxed">
            PDF, JPG oder PNG, jeweils bis 8 MB. Deine Dokumente gehen ausschließlich an mich und werden
            nur nach vorheriger Absprache mit dir an einen Betrieb weitergegeben.
          </p>
          {isAusland && (
            <p className="text-[11px] text-[#9E5841] mt-3 leading-relaxed" data-testid="koch-ausland-hinweis">
              Du bist im Ausland gemeldet: Lade bitte die Nachweise deines Heimatlandes hoch
              (Gewerbe-/Unternehmensnachweis, Hygieneschulung). Für Einsätze in Deutschland oder Österreich
              brauchst du als Selbstständiger in der Regel eine A1-Bescheinigung — liegt sie schon vor,
              lade sie gerne gleich mit hoch. Fehlt dir ein Nachweis, kreuze „wird nachgereicht" an;
              die Registrierung geht trotzdem ab.
            </p>
          )}
          {isAT && (
            <p className="text-[11px] text-[#9E5841] mt-3 leading-relaxed" data-testid="koch-at-hinweis">
              Hinweis für Einsätze in Deutschland: Als selbstständiger Koch aus Österreich brauchst du dafür
              in der Regel eine A1-Bescheinigung, je nach Fall auch eine Dienstleistungsanzeige. Ich sage dir
              vor jedem grenzüberschreitenden Einsatz rechtzeitig Bescheid.
            </p>
          )}
          <div className="grid sm:grid-cols-2 gap-5 mt-6">
            {docs.map((d) => (
              <FileField key={d.key} doc={d} file={files[d.key]}
                onPick={(f) => {
                  setFiles((s) => ({ ...s, [d.key]: f }));
                  if (d.key === "gesundheitszeugnis") setIfsgLater(false);
                  if (d.key === "gewerbeanmeldung") setGewerbeLater(false);
                }}
                disabled={(d.key === "gesundheitszeugnis" && ifsgLater)
                  || (d.key === "gewerbeanmeldung" && gewerbeLater)}>
                {d.key === "gewerbeanmeldung" && (
                  <label className="flex items-start gap-3 mt-3 cursor-pointer" data-testid="koch-gewerbe-later-label">
                    <input type="checkbox" className="mt-0.5 accent-[#BA6D52]" checked={gewerbeLater}
                      data-testid="koch-gewerbe-later"
                      onChange={(e) => {
                        setGewerbeLater(e.target.checked);
                        if (e.target.checked) setFiles((s) => ({ ...s, gewerbeanmeldung: undefined }));
                      }} />
                    <span className="text-[11px] text-[#575349] leading-relaxed">
                      Wird nachgereicht — ich bin im Ausland gemeldet oder meine Anmeldung läuft noch.
                      Ich schicke den Nachweis später per E-Mail nach. Die Registrierung geht trotzdem ab.
                    </span>
                  </label>
                )}
                {d.key === "gesundheitszeugnis" && (
                  <label className="flex items-start gap-3 mt-3 cursor-pointer" data-testid="koch-ifsg-later-label">
                    <input type="checkbox" className="mt-0.5 accent-[#BA6D52]" checked={ifsgLater}
                      data-testid="koch-ifsg-later"
                      onChange={(e) => {
                        setIfsgLater(e.target.checked);
                        if (e.target.checked) setFiles((s) => ({ ...s, gesundheitszeugnis: undefined }));
                      }} />
                    <span className="text-[11px] text-[#575349] leading-relaxed">
                      Wird nachgereicht — ich schicke die Belehrung § 43 IfSG später per E-Mail nach.
                      Die Registrierung geht trotzdem ab.
                    </span>
                  </label>
                )}
              </FileField>
            ))}
          </div>
          {ifsgLater && (
            <p className="text-[11px] text-[#9E5841] mt-4 leading-relaxed" data-testid="koch-ifsg-later-hint">
              Hinweis: Einteilen kann ich dich erst, wenn mir die Belehrung § 43 IfSG vorliegt —
              Betriebe müssen sie im Haus haben.
            </p>
          )}
          {gewerbeLater && (
            <p className="text-[11px] text-[#9E5841] mt-3 leading-relaxed" data-testid="koch-gewerbe-later-hint">
              Hinweis: Vermitteln kann ich dich erst, wenn mir dein Gewerbenachweis vorliegt —
              bei einer Anmeldung im Ausland genügt der Nachweis deines Heimatlandes.
            </p>
          )}
        </section>

        <section>
          <h2 className="text-base md:text-lg font-serif border-b border-[#E3DECE] pb-3">Vermittlungsvertrag</h2>
          <p className="text-sm text-[#757067] mt-4 leading-relaxed">
            Kurz und ohne Kleingedrucktes: Ich vermittle, du entscheidest frei über jeden Einsatz, du rechnest
            selbst ab — und die Provision zahlt immer der Betrieb, nie du.
          </p>
          <a href={`${API}/vermittlung/vertrag/mietkoch.pdf`} target="_blank" rel="noreferrer"
            data-testid="koch-vertrag-download"
            className="inline-flex items-center gap-2 mt-5 border border-[#BA6D52] text-[#BA6D52] hover:bg-[#FAF5F1] px-5 py-3 text-[11px] uppercase tracking-[0.2em] transition-colors">
            <FileDown className="w-4 h-4" /> Vertrag als PDF lesen
          </a>

          <div className="flex gap-2 mt-8">
            {[["digital", "Digital unterschreiben"], ["upload", "Unterschrieben hochladen"]].map(([k, label]) => (
              <button type="button" key={k} onClick={() => setVertragMode(k)}
                data-testid={`koch-vertrag-mode-${k}`}
                className={`px-5 py-3 text-[11px] uppercase tracking-[0.15em] border transition-colors ${vertragMode === k ? "bg-[#2C2A26] text-white border-[#2C2A26]" : "bg-white border-[#E3DECE] text-[#575349] hover:border-[#BA6D52]"}`}>
                {label}
              </button>
            ))}
          </div>

          {vertragMode === "digital" ? (
            <div className="mt-6 bg-[#F2EFE9] p-6">
              <label className={LABEL}>Vollständiger Name (wie im Vertrag)</label>
              <input className={`${FIELD} max-w-sm`} value={form.signature_name} onChange={set("signature_name")}
                placeholder={`${form.vorname} ${form.nachname}`.trim()} data-testid="koch-signature-name" />
              <p className={`${LABEL} mt-6`}>Deine Unterschrift</p>
              <SignaturePad onChange={setSignature} testId="koch-signature" />
              <p className="text-xs text-[#757067] mt-4 leading-relaxed">
                Mit dem Absenden erkläre ich mich mit dem Vermittlungs- und Koordinationsvertrag einverstanden.
                Du erhältst das fertig unterschriebene PDF per E-Mail zurück.
              </p>
            </div>
          ) : (
            <div className="mt-6 max-w-md">
              <FileField
                doc={{ key: "vermittlungsvertrag", label: "Unterschriebener Vertrag", hint: "Ausgedruckt, unterschrieben, eingescannt oder abfotografiert" }}
                file={files.vermittlungsvertrag}
                onPick={(f) => setFiles((s) => ({ ...s, vermittlungsvertrag: f }))}
              />
            </div>
          )}
        </section>

        <label className="flex items-start gap-3 text-sm text-[#575349] cursor-pointer">
          <input type="checkbox" className="mt-1 accent-[#BA6D52]" checked={form.dsgvo_consent} onChange={set("dsgvo_consent")} data-testid="koch-dsgvo" />
          <span>
            Ich habe die <a href="/datenschutz" target="_blank" rel="noreferrer" className="text-[#BA6D52] underline">Datenschutzhinweise</a> gelesen
            und bin damit einverstanden, dass meine Daten und Unterlagen zum Zweck der Vermittlung verarbeitet werden. *
          </span>
        </label>

        <button type="submit" disabled={busy} data-testid="koch-submit"
          className="w-full sm:w-auto bg-[#BA6D52] text-white hover:bg-[#9E5841] disabled:opacity-60 px-10 py-4 text-[11px] uppercase tracking-[0.25em] transition-colors">
          {busy ? "Wird übertragen …" : "Registrierung absenden"}
        </button>
      </form>
    </div>
  );
}

const FileField = ({ doc, file, onPick, disabled = false, children }) => {
  const [over, setOver] = useState(false);
  const stop = (e) => { e.preventDefault(); e.stopPropagation(); };
  const ALLOWED = ["pdf", "jpg", "jpeg", "png", "webp"];
  const handleDrop = (e) => {
    stop(e);
    setOver(false);
    if (disabled) return;
    const f = e.dataTransfer?.files?.[0];
    if (!f) return;
    const ext = f.name.split(".").pop()?.toLowerCase();
    if (!ALLOWED.includes(ext)) {
      toast.error("Bitte eine PDF-Datei oder ein Foto (JPG, PNG, WEBP) ablegen");
      return;
    }
    onPick(f);
  };
  return (
    <div>
      <label className={LABEL}>{doc.label} {doc.required && "*"}</label>
      <label
        data-testid={`file-field-${doc.key}`}
        data-file-drop="1"
        onDragEnter={(e) => { stop(e); setOver(true); }}
        onDragOver={(e) => { stop(e); setOver(true); }}
        onDragLeave={(e) => { stop(e); setOver(false); }}
        onDrop={handleDrop}
        className={`flex items-center gap-3 border border-dashed px-4 py-4 transition-colors ${
          disabled ? "border-[#E3DECE] bg-[#F5F2EC] opacity-60 cursor-not-allowed"
            : over ? "border-[#BA6D52] bg-[#F2EFE9] ring-2 ring-[#BA6D52]/30 cursor-pointer"
            : file ? "border-[#BA6D52] bg-[#FAF5F1] cursor-pointer" : "border-[#E3DECE] bg-white hover:border-[#BA6D52] cursor-pointer"}`}
      >
        {file ? <Check className="w-4 h-4 text-[#BA6D52] shrink-0" /> : <Upload className="w-4 h-4 text-[#757067] shrink-0" />}
        <span className="text-xs text-[#575349] truncate">
          {disabled ? "Wird nachgereicht" : over ? "Jetzt loslassen" : file ? file.name : "Datei auswählen oder hierher ziehen"}
        </span>
        <input type="file" accept=".pdf,.jpg,.jpeg,.png,.webp" className="hidden" disabled={disabled}
          data-testid={`file-input-${doc.key}`}
          onChange={(e) => e.target.files?.[0] && onPick(e.target.files[0])} />
      </label>
      <p className="text-[11px] text-[#757067] mt-2">{doc.hint}</p>
      {children}
    </div>
  );
};
